import { window } from "global";
import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { MediaQuery } from "@outplayed/responsive";
import { TierListHeader } from "@ugg/shared/components/TierList/TierListHeader";
import { TierListPageNav } from "@ugg/shared/components/TierList/TierListPageNav";
import { TierListFilterManager } from "@ugg/shared/components/TierList/TierListFilterManager";
import { TierListTablePlaceholder } from "@ugg/shared/components/TierList/common/TierListTablePlaceholder";
import { TierListTableError } from "@ugg/shared/components/TierList/common/TierListTableError";
import { SPECIAL_TIER_LIST_PAGES } from "@ugg/shared/pages/tier-list-pages";
import { useValidatedTierListParams } from "@ugg/shared/components/TierList/common/use-validated-tier-list-params";
import { buildQueryParams } from "@ugg/shared/utils/url-query-params";
import {
  ClassicChampionsManifest,
  ClassicTierEntry,
  getClassicChampionImgUrl,
  getClassicRoleFromLetter,
  getClassicRoleLetter,
  getClassicRoleName,
  useClassicChampions,
  resolveClassicAssetVersion,
  useClassicChampionTierList,
} from "@ugg/shared/hooks/classic";
import { getRoleSVG } from "@ugg/shared/utils/role-helpers";
import { getClassicChampionBuildUrl } from "@ugg/shared/routes/app-routes";

import { ALL_TIERS, TierNameValue } from "@ugg/shared/utils/tier-helpers";
// Shared tier-group card — the Classic tier list uses the same visual
// treatment as the ARAM Mayhem ones (colored banner + divider + zebra-striped
// body), so they stay in lockstep by construction rather than by convention.
import { TierGroupCard } from "@ugg/shared/components/TierList/common/TierGroupCard";
import { AD_RAIL_MIN_CONTENT_HEIGHT } from "@ugg/shared/components/TierList/common/min-content-height";
import { asArray, asText } from "@ugg/shared/utils/safe-coerce";

type TierListContainerProps = {
  bannerSlot?: React.ReactNode;
  patchNotesCTA?: React.ReactNode;
};

// ─────────────────────────────────────────────────────────────────────
// Disclaimer-banner scaffolding (currently unused)
// ─────────────────────────────────────────────────────────────────────
//
// RETAINED ON PURPOSE — do not "clean up" as dead code.
//
// This was the icon for the PBE disclaimer that sat above the tier list while
// Classic data was PBE-sourced (16_14 only). That CTA was removed once Classic
// went live, since users can now assume 16_15+ data is final. The icon is kept
// because a disclaimer banner is a recurring need on these pages and this is
// the piece that isn't trivial to re-derive; recover the card itself from the
// commit that removed it.
//
// There is no bundle cost: nothing imports this file's unused members, so the
// function is tree-shaken out. Note that `tsconfig` here does NOT set
// `noUnusedLocals`, so nothing will warn about it either way.
//
// Inline 4-point sparkle so the graphic doesn't depend on the RemixIcon
// font being loaded in every render context (the site loads it globally,
// the desktop app may not).
function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M12 1c1.1 4.9 2 5.8 6.9 6.9C14 9 13.1 9.9 12 14.8 10.9 9.9 10 9 5.1 7.9 10 6.8 10.9 5.9 12 1Z" />
      <path d="M18.5 14c.5 2.3.9 2.7 3.2 3.2-2.3.5-2.7.9-3.2 3.2-.5-2.3-.9-2.7-3.2-3.2 2.3-.5 2.7-.9 3.2-3.2Z" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Table
// ─────────────────────────────────────────────────────────────────────

interface ClassicChampionTierListTableProps {
  // Partial: absent tiers are omitted from the payload (see ClassicChampionTierList).
  // The table already guards each tier before mapping, so undefined is expected.
  tierlist: Partial<Record<TierNameValue, ClassicTierEntry[]>>;
  manifest: ClassicChampionsManifest;
  // Query string (e.g. "patch=16_14") carried onto each champion's build link so
  // clicking through keeps the currently-selected patch — mirrors the shared
  // ChampionLink, which passes the tier list's URL params to the build page.
  // Empty on the latest patch (no `?patch=`), so canonical links stay clean.
  championLinkSearch?: string;
  // Resolved <version> for the versioned Classic asset CDN, used for the
  // portraits. Undefined until the patch resolves; a portrait can't be built
  // without it, so the tile drops the <img> and keeps its 36px box.
  assetVersion?: string;
}

export function ClassicChampionTierListTable(props: ClassicChampionTierListTableProps) {
  const { tierlist, manifest, championLinkSearch, assetVersion } = props || {};

  return (
    <div className="flex flex-col gap-6">
      {ALL_TIERS.map((tier) => {
        // Normalize to well-formed [key, role] entries up front so a malformed
        // tier payload (non-array tier, or entries that aren't [key, role] pairs)
        // degrades to empty rows instead of throwing on the destructures below.
        const champions = asArray(tierlist[tier])
          .filter((entry) => Array.isArray(entry))
          .map((entry) => [entry[0], asText(entry[1])] as ClassicTierEntry);
        if (champions.length === 0) return null;
        // Sort each tier alphabetically by champion display name to mirror
        // the tier-list building tool's non-manual ordering. Falls back to
        // the raw key for champions missing from the metadata manifest, and
        // breaks name ties by role letter so the order stays stable.
        const sortedChampions = [...champions].sort(([keyA, roleA], [keyB, roleB]) => {
          const nameA = asText(manifest[String(keyA)]?.[1]) || `#${keyA}`;
          const nameB = asText(manifest[String(keyB)]?.[1]) || `#${keyB}`;
          return nameA.localeCompare(nameB) || roleA.localeCompare(roleB);
        });
        return (
          <TierGroupCard key={tier} tier={tier}>
            {sortedChampions.map(([championKey, role]) => {
              const displayName = manifest[String(championKey)]?.[1];
              const alias = manifest[String(championKey)]?.[0];

              // No display name yet (manifest still loading / champion
              // missing from the metadata file) — fall back to the raw
              // key so the row still renders and the layout holds.
              if (!displayName) {
                return (
                  <div key={`${championKey}-${role}`} className="flex items-center gap-2">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] border border-dashed border-[#1c2030] bg-[#1a1d2e] font-mono text-[10px] text-[#64748b]">
                      ?
                    </div>
                    <span className="font-mono text-sm text-[#cddcea]">
                      #{championKey} [{role}]
                    </span>
                  </div>
                );
              }

              // Champion images come from the Classic custom CDN (not
              // riot-assets). White role icon shared with the filter chips
              // (getRoleSVG default variant), resolved from the tier-list role
              // letter. Rendered between the portrait and the name.
              // `?? ""` because an unrecognized letter has no site role;
              // getRoleSVG falls through its switch and returns nothing, so
              // the icon is simply omitted (the previous local map produced
              // the same runtime behavior, it just wasn't typed for it).
              const roleIcon = getRoleSVG(getClassicRoleFromLetter(role) ?? "");

              const tileContent = (
                <>
                  {assetVersion ? (
                    <img
                      src={getClassicChampionImgUrl(championKey, assetVersion)}
                      alt={displayName}
                      title={displayName}
                      loading="lazy"
                      className="h-9 w-9 shrink-0 rounded-[3px]"
                    />
                  ) : (
                    <div className="h-9 w-9 shrink-0 rounded-[3px]" />
                  )}
                  {roleIcon && (
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center" title={getClassicRoleName(role)}>
                      {roleIcon}
                    </span>
                  )}
                  <span className="font-[Barlow] text-sm font-semibold text-white group-hover/champion-link:underline">
                    {displayName}
                  </span>
                </>
              );

              // Link each champion to its Classic build page. Falls back to a
              // plain (non-link) tile if the alias is missing from the manifest.
              return alias ? (
                <Link
                  key={`${championKey}-${role}`}
                  to={{ pathname: getClassicChampionBuildUrl(alias), search: championLinkSearch }}
                  className="group/champion-link flex items-center gap-2"
                >
                  {tileContent}
                </Link>
              ) : (
                <div key={`${championKey}-${role}`} className="flex items-center gap-2">
                  {tileContent}
                </div>
              );
            })}
          </TierGroupCard>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Container
// ─────────────────────────────────────────────────────────────────────

export const ClassicChampionTierListContainer = ({ bannerSlot, patchNotesCTA }: TierListContainerProps) => {
  const {
    queryParams,
    validated: validatedQueryParams,
    patch,
  } = useValidatedTierListParams(SPECIAL_TIER_LIST_PAGES.CLASSIC_TIER_LIST);

  // Query string carried onto each champion's Classic build link so clicking a
  // champion keeps the selected patch (e.g. `?patch=16_14` → the build page's
  // version filter). Only carried when the URL explicitly has a `?patch=` — on
  // the latest patch there's no param, so links stay canonical (no `?patch=`).
  // Uses the VALIDATED patch (not the raw value) so a bad param can't leak
  // through. Patch only (not role/region/queue): the Classic build page takes
  // role as a path segment, so passing the other filters as query would just
  // clutter the URL. Matches the shared ChampionLink's param pass-through.
  const championLinkSearch = queryParams.patch ? buildQueryParams({ patch }) : "";

  // Champion metadata (key → [alias, displayName]) for the Classic
  // roster. Fetched with `ssr: true` so the names are prefetched during
  // server render and hydrate cleanly on the client (SSR + JSONFetcher
  // handling mirrors the other special-mode data hooks). Version-scoped like
  // the portraits below, so it takes the validated patch.
  const { data: manifestData, loading: manifestLoading, error: manifestError } = useClassicChampions({ patch, ssr: true });
  const manifest = manifestData || ({} as ClassicChampionsManifest);

  // Tier list data — per-patch file from the Classic CDN pipeline. The
  // patch filter swaps the version segment of the URL.
  const {
    data: tierlistData,
    loading: tierlistLoading,
    error: tierlistError,
  } = useClassicChampionTierList({ params: queryParams, ssr: true });
  const { tierlist, lastUpdated } = tierlistData || {};

  // Role filter. "All Roles" (the default) shows every champion/role
  // entry; picking a lane narrows each tier to the entries tagged with
  // that role letter. getClassicRoleLetter resolves the query value —
  // including the SEO "adc" alias for bot — to the tier-list letter, and
  // returns undefined for "all", meaning "don't filter".
  const roleLetter = getClassicRoleLetter(validatedQueryParams.role);
  const filteredTierlist = useMemo(() => {
    const base = tierlist || ({} as Record<TierNameValue, ClassicTierEntry[]>);
    if (!roleLetter) return base;
    const result = {} as Record<TierNameValue, ClassicTierEntry[]>;
    for (const tier of ALL_TIERS) {
      result[tier] = asArray(base[tier]).filter((entry) => asText(entry?.[1]).toUpperCase() === roleLetter);
    }
    return result;
  }, [tierlist, roleLetter]);

  // Champion name search — layered on top of the role filter, so it narrows
  // within the current role selection (this scoping may change based on
  // feedback). Case-insensitive substring match against the display name; an
  // empty query is a no-op. Driven by the `search` query param, rendered by
  // TierListFilterManager, so a filtered view is shareable and survives reload.
  const search = asText(validatedQueryParams.search);
  const searchedTierlist = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return filteredTierlist;
    const result = {} as Record<TierNameValue, ClassicTierEntry[]>;
    for (const tier of ALL_TIERS) {
      result[tier] = asArray(filteredTierlist[tier]).filter((entry) => {
        const name = manifest[String(entry?.[0])]?.[1];
        return typeof name === "string" ? name.toLowerCase().includes(query) : false;
      });
    }
    return result;
  }, [filteredTierlist, manifest, search]);

  // Gate the table on BOTH fetches: the tier list supplies the rows and
  // the manifest supplies the champion names, so defer until both are
  // ready rather than flashing placeholder names then the real ones.
  const loading = manifestLoading || tierlistLoading;
  const error = manifestError || tierlistError;

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  // Both the manifest and the tier list are prefetched with `ssr: true`, so
  // they're present during server render — we render the table server-side for
  // SEO (mirroring the Classic home grid) rather than gating on `window`. Server
  // and client first render match (same prefetched data hydrated from the SSR
  // cache), so there's no hydration mismatch; `loading="lazy"` imgs are valid in
  // SSR markup. Falls back to placeholder/error the same way while data resolves.
  let table;
  if (loading) {
    table = <TierListTablePlaceholder />;
  } else if (error) {
    table = <TierListTableError />;
  } else {
    table = (
      <ClassicChampionTierListTable
        tierlist={searchedTierlist}
        manifest={manifest}
        championLinkSearch={championLinkSearch}
        assetVersion={resolveClassicAssetVersion(patch)}
      />
    );
  }

  return (
    // Keeps the sticky ad rails from drifting up when the list filters down —
    // see AD_RAIL_MIN_CONTENT_HEIGHT.
    <div className={AD_RAIL_MIN_CONTENT_HEIGHT}>
      <TierListHeader
        className="mb-[24px]"
        totalMatches={0}
        lastUpdated={lastUpdated}
        loading={loading}
        includeMeta
        patchNotesCTA={patchNotesCTA}
      />
      <MediaQuery min="TABLET" max="DESKTOP_LARGE">
        <div>
          <TierListPageNav />
        </div>
      </MediaQuery>
      {bannerSlot}
      <TierListFilterManager className="my-[24px]" />
      <>{table}</>
    </div>
  );
};
